import { useState, useRef, useEffect, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { useQuery } from "@tanstack/react-query";
import {
  Bug,
  ChevronDown,
  Trash2,
  Copy,
  Check,
  ExternalLink,
  Wallet,
  FileCode,
  ScrollText,
  Circle,
  Loader2,
} from "lucide-react";
import { TelegramIcon } from "@/components/icons/TelegramIcon";
import { cn } from "@/lib/utils";
import { useDebugStore, type DebugLogEntry } from "@/stores/useDebugStore";
import { useAuthStore } from "@/stores/useAuthStore";
import { usePolygonClient } from "@/hooks/usePolygonClient";
import { useCopyToClipboard } from "@/hooks/useCopyToClipboard";
import { useWalletBalances } from "@/hooks/useWalletBalances";
import { getChainConfig } from "@/config/contracts";
import { allContractCalls, protocolContracts, tokenContracts } from "@/lib/debugContracts";
import { useNotificationChannels, getChannelDisplayName } from "@/features/channels/hooks/useNotificationChannels";
import { useTier } from "@/hooks/useTier";
import { can } from "@/lib/tier";
import { useDraggable, type DraggablePosition } from "@/hooks/useDraggable";

// ─── Helpers ────────────────────────────────────────────

function formatTime(iso: string): string {
  const d = new Date(iso);
  const h = String(d.getHours()).padStart(2, "0");
  const m = String(d.getMinutes()).padStart(2, "0");
  const s = String(d.getSeconds()).padStart(2, "0");
  const ms = String(d.getMilliseconds()).padStart(3, "0");
  return `${h}:${m}:${s}.${ms}`;
}

function shortenAddr(addr: string, chars = 6): string {
  if (addr.length <= chars * 2 + 3) return addr;
  return `${addr.slice(0, chars)}...${addr.slice(-chars)}`;
}

type Tab = "account" | "contracts" | "logs";

/** Once dragged, pin the element to absolute viewport coords instead of its CSS anchor. */
function draggedStyle(drag: { position: DraggablePosition | null }): React.CSSProperties | undefined {
  if (!drag.position) return undefined;
  return { left: drag.position.x, top: drag.position.y, right: "auto", bottom: "auto" };
}

// ─── Copy button ────────────────────────────────────────

function CopyBtn({ text, size = "sm" }: { text: string; size?: "sm" | "md" }) {
  const { t } = useTranslation();
  const { copied, copy } = useCopyToClipboard();
  const iconSize = size === "md" ? "h-4 w-4" : "h-3.5 w-3.5";

  return (
    <button
      onClick={(e) => {
        e.stopPropagation();
        copy(text);
      }}
      className="inline-flex items-center rounded-md p-1 text-neutral-300 hover:text-white hover:bg-neutral-600/50 active:scale-75 transition-all duration-150"
      title={copied ? t("debug.copied") : t("debug.copyToClipboard")}
      aria-label={copied ? t("debug.copied") : t("debug.copyToClipboard")}
    >
      {copied ? (
        <Check className={cn(iconSize, "text-emerald-400")} />
      ) : (
        <Copy className={iconSize} />
      )}
    </button>
  );
}

// ─── Explorer link ──────────────────────────────────────

function ExplorerLink({ addr, type = "address" }: { addr: string; type?: "address" | "tx" }) {
  const { t } = useTranslation();
  const cfg = getChainConfig();
  const url = `${cfg.blockExplorer}/${type}/${addr}`;
  return (
    <a
      href={url}
      target="_blank"
      rel="noopener noreferrer"
      onClick={(e) => e.stopPropagation()}
      className="inline-flex items-center rounded-md p-1 text-neutral-300 hover:text-white hover:bg-neutral-600/50 active:scale-75 transition-all duration-150"
      title={t("debug.viewOnExplorer")}
      aria-label={t("debug.viewOnExplorer")}
    >
      <ExternalLink className="h-3.5 w-3.5" />
    </a>
  );
}

// ─── Address display ────────────────────────────────────

function AddressField({ label, address, badge }: { label: string; address: string; badge?: string }) {
  return (
    <div className="rounded-lg bg-neutral-800/80 border border-neutral-700/40 px-3.5 py-2.5">
      <div className="flex items-center justify-between mb-1.5">
        <span className="text-[12px] font-semibold text-neutral-300 uppercase tracking-wider">{label}</span>
        {badge && (
          <span className="rounded-full bg-brand-500/25 border border-brand-400/30 px-2 py-0.5 text-[11px] font-semibold text-brand-200">
            {badge}
          </span>
        )}
      </div>
      <div className="flex items-center gap-1.5">
        <code className="text-[14px] text-white font-mono flex-1 truncate" title={address}>
          {shortenAddr(address, 8)}
        </code>
        <CopyBtn text={address} size="md" />
        <ExplorerLink addr={address} />
      </div>
    </div>
  );
}

// ─── Contract row ───────────────────────────────────────

function ContractRow({ label, address, calls = 0 }: { label: string; address: string; calls?: number }) {
  return (
    <div className="flex items-center justify-between gap-3 rounded-lg px-3.5 py-2.5 hover:bg-neutral-800/60 transition-colors group">
      <span className="flex items-center gap-2 text-[14px] font-medium text-neutral-100">
        {label}
        {calls > 0 && (
          <span className="rounded-full border border-purple-400/30 bg-purple-500/20 px-2 py-px text-[11px] font-semibold text-purple-200 tabular-nums">
            ×{calls}
          </span>
        )}
      </span>
      <div className="flex items-center gap-1.5">
        <code className="text-[13px] text-neutral-300 font-mono group-hover:text-white transition-colors">
          {shortenAddr(address, 6)}
        </code>
        <CopyBtn text={address} />
        <ExplorerLink addr={address} />
      </div>
    </div>
  );
}

// ─── Account Info Tab ───────────────────────────────────

function AccountTab() {
  const { t } = useTranslation();
  const walletAddress = useAuthStore((s) => s.session?.walletAddress);
  const userId = useAuthStore((s) => s.session?.userId);
  const polygonClient = usePolygonClient();
  const { data: balances } = useWalletBalances();
  const { data: channels = [] } = useNotificationChannels();
  const telegramChannel = channels.find((c) => c.provider === "telegram" && !c.revoked_at);

  const { data: eoaAddress } = useQuery({
    // The signer is the session user's embedded wallet.
    queryKey: ["debug-eoa-address", userId],
    enabled: !!polygonClient,
    staleTime: 60_000,
    queryFn: async () => polygonClient!.getAddress(),
  });

  const { data: isRegistered } = useQuery({
    queryKey: ["debug-on-chain-registered", walletAddress],
    enabled: !!polygonClient && !!walletAddress,
    staleTime: 60_000,
    queryFn: async () => polygonClient!.isRegistered(walletAddress!),
  });

  const cfg = getChainConfig();

  if (!walletAddress) {
    return (
      <div className="flex flex-col items-center justify-center py-10 gap-3">
        <Wallet className="h-10 w-10 text-neutral-500" />
        <p className="text-sm text-neutral-300 font-medium">{t("debug.walletNotConnected")}</p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Wallet addresses */}
      <div className="space-y-2">
        {eoaAddress ? (
          <AddressField label={t("debug.eoaSigner")} address={eoaAddress} />
        ) : (
          <div className="rounded-lg bg-neutral-800/80 border border-neutral-700/40 px-3.5 py-2.5">
            <span className="text-[12px] font-semibold text-neutral-300 uppercase tracking-wider">{t("debug.eoaSigner")}</span>
            <div className="flex items-center gap-2 mt-1.5">
              <Loader2 className="h-4 w-4 text-brand-400 animate-spin" />
              <span className="text-[13px] text-brand-300 font-medium">{t("debug.loading")}</span>
            </div>
          </div>
        )}
        <AddressField label={t("debug.embeddedWallet")} address={walletAddress} badge={t("debug.walletType")} />
      </div>

      {/* Chain + On-chain status */}
      <div className="rounded-lg bg-neutral-800/80 border border-neutral-700/40 px-3.5 py-3">
        <div className="flex items-center justify-between mb-2.5">
          <span className="text-[13px] font-medium text-neutral-200">{t("debug.chainInfo")}</span>
          <div className="flex items-center gap-1.5">
            <Circle className="h-2.5 w-2.5 fill-emerald-400 text-emerald-400" />
            <span className="text-[14px] font-semibold text-white">
              {cfg.name} ({cfg.chainId})
            </span>
          </div>
        </div>
        <div className="flex items-center justify-between">
          <span className="text-[13px] font-medium text-neutral-200">{t("debug.onChainStatus")}</span>
          {isRegistered == null ? (
            <div className="flex items-center gap-1.5">
              <Loader2 className="h-3.5 w-3.5 text-brand-400 animate-spin" />
              <span className="text-[13px] text-brand-300 font-medium">{t("debug.checking")}</span>
            </div>
          ) : (
            <span
              className={cn(
                "text-[14px] font-semibold",
                isRegistered ? "text-emerald-400" : "text-red-400",
              )}
            >
              {isRegistered ? t("debug.registered") : t("debug.notRegistered")}
            </span>
          )}
        </div>
      </div>

      {/* Telegram */}
      <div className="rounded-lg bg-neutral-800/80 border border-neutral-700/40 px-3.5 py-3">
        <div className="flex items-center justify-between">
          <span className="text-[13px] font-medium text-neutral-200">Telegram</span>
          {telegramChannel ? (
            <div className="flex items-center gap-1.5">
              <TelegramIcon className="h-4 w-4 text-[#26A5E4]" />
              <span className="text-[13px] font-medium text-emerald-400">
                {getChannelDisplayName(telegramChannel) ?? t("profile.channels.connected")}
              </span>
            </div>
          ) : (
            <div className="flex items-center gap-1.5">
              <TelegramIcon className="h-4 w-4 text-[#26A5E4]" />
              <span className="text-[13px] font-medium text-[#26A5E4]">
                {t("debug.notConnected", "Not connected")}
              </span>
            </div>
          )}
        </div>
      </div>

      {/* Balances */}
      {balances && (
        <div className="rounded-lg bg-neutral-800/80 border border-neutral-700/40 px-3.5 py-3">
          <p className="text-[12px] font-semibold text-neutral-300 uppercase tracking-wider mb-2.5">
            {t("debug.balances")}
          </p>
          <div className="grid grid-cols-3 gap-3">
            {[
              { label: "USDT", value: balances.usdt.toFixed(2) },
              { label: "USDC (legacy)", value: balances.usdc.toFixed(2) },
              { label: "POL", value: balances.pol.toFixed(4) },
            ].map((b) => (
              <div key={b.label} className="text-center rounded-lg bg-neutral-900/60 border border-neutral-700/30 py-2.5">
                <p className="text-[16px] font-bold text-white font-mono">{b.value}</p>
                <p className="text-[12px] text-neutral-300 font-medium mt-0.5">{b.label}</p>
              </div>
            ))}
          </div>
          <p className="text-right text-[13px] text-neutral-300 mt-2.5">
            {t("debug.totalUsd")}: <span className="text-white font-bold">${balances.totalUsd.toFixed(2)}</span>
          </p>
        </div>
      )}
    </div>
  );
}

// ─── Contracts Tab ──────────────────────────────────────

function ContractsTab() {
  const { t } = useTranslation();
  const logs = useDebugStore((s) => s.logs);
  const calls = allContractCalls(logs);
  const callCount = (label: string) => calls.filter((c) => c.contract === label).length;

  return (
    <div className="space-y-5">
      <div>
        <p className="text-[12px] font-semibold text-neutral-300 uppercase tracking-wider px-3.5 mb-1.5">{t("debug.calledThisSession")}</p>
        {calls.length === 0 ? (
          <p className="px-3.5 text-[13px] text-neutral-300">{t("debug.noContractsCalled")}</p>
        ) : (
          <ul className="space-y-0.5">
            {calls.map((c, i) => (
              <li key={`${c.txHash}-${i}`} className="flex items-center justify-between gap-3 rounded-lg px-3.5 py-2 hover:bg-neutral-800/60 transition-colors">
                <code className="text-[13px] text-neutral-100 font-mono break-all">{c.contract}.{c.method}</code>
                {c.txHash && <ExplorerLink addr={c.txHash} type="tx" />}
              </li>
            ))}
          </ul>
        )}
      </div>
      <div>
        <p className="text-[12px] font-semibold text-neutral-300 uppercase tracking-wider px-3.5 mb-1.5">{t("debug.protocol")}</p>
        <div className="space-y-0.5">
          {protocolContracts().map((c) => (
            <ContractRow key={c.label} label={c.label} address={c.address} calls={callCount(c.label)} />
          ))}
        </div>
      </div>
      <div>
        <p className="text-[12px] font-semibold text-neutral-300 uppercase tracking-wider px-3.5 mb-1.5">{t("debug.tokens")}</p>
        <div className="space-y-0.5">
          {tokenContracts().map((c) => (
            <ContractRow key={c.label} label={c.label} address={c.address} calls={callCount(c.label)} />
          ))}
        </div>
      </div>
    </div>
  );
}

// ─── Log Entry ──────────────────────────────────────────

const LEVEL_DOT: Record<string, string> = {
  info: "fill-blue-400 text-blue-400",
  warn: "fill-amber-300 text-amber-300",
  error: "fill-red-400 text-red-400",
};

const CATEGORY_STYLE: Record<string, string> = {
  chain: "bg-purple-500/20 text-purple-200 border-purple-400/30",
  db: "bg-cyan-500/20 text-cyan-200 border-cyan-400/30",
  tx: "bg-blue-500/20 text-blue-200 border-blue-400/30",
};

const LEVEL_ROW_BG: Record<string, string> = {
  error: "bg-red-950/40 border-l-2 border-l-red-500",
  warn: "bg-amber-950/20 border-l-2 border-l-amber-500",
  info: "border-l-2 border-l-transparent",
};

const LEVEL_MSG_COLOR: Record<string, string> = {
  error: "text-red-300",
  warn: "text-amber-200",
  info: "text-white",
};

function LogEntry({ entry }: { entry: DebugLogEntry }) {
  const { t } = useTranslation();
  const [expanded, setExpanded] = useState(false);

  return (
    <div
      className={cn(
        "px-3.5 py-2.5 cursor-pointer transition-colors border-b border-neutral-700/40",
        LEVEL_ROW_BG[entry.level] ?? "border-l-2 border-l-transparent",
        expanded
          ? entry.level === "error" ? "bg-red-950/50" : "bg-neutral-800/50"
          : entry.level === "error" ? "hover:bg-red-950/50" : "hover:bg-neutral-800/30",
      )}
      onClick={() => setExpanded(!expanded)}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") setExpanded(!expanded); }}
      aria-expanded={expanded}
    >
      <div className="flex items-start gap-2.5">
        <Circle className={cn("h-2.5 w-2.5 mt-[5px] shrink-0", LEVEL_DOT[entry.level] ?? "fill-neutral-300 text-neutral-300")} />
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1">
            <span className={cn(
              "text-[12px] font-mono tabular-nums",
              entry.level === "error" ? "text-red-300/80" : "text-neutral-300",
            )}>{formatTime(entry.timestamp)}</span>
            <span
              className={cn(
                "rounded-full border px-2 py-px text-[11px] font-semibold uppercase",
                CATEGORY_STYLE[entry.category] ?? "bg-neutral-700/50 text-neutral-300 border-neutral-500/30",
              )}
            >
              {entry.category}
            </span>
            {entry.level === "error" && (
              <span className="rounded-full bg-red-500/25 border border-red-400/40 px-2 py-px text-[11px] font-bold text-red-300 uppercase">
                {t("debug.error")}
              </span>
            )}
            {entry.level === "warn" && (
              <span className="rounded-full bg-amber-500/20 border border-amber-400/30 px-2 py-px text-[11px] font-bold text-amber-300 uppercase">
                {t("debug.warn")}
              </span>
            )}
          </div>
          <p className={cn("text-[14px] leading-snug break-words", LEVEL_MSG_COLOR[entry.level] ?? "text-white")}>
            {entry.message}
          </p>
        </div>
      </div>
      {expanded && entry.meta && (
        <pre className={cn(
          "mt-2.5 ml-5 rounded-lg px-3.5 py-2.5 text-[12px] whitespace-pre-wrap break-all overflow-x-auto",
          entry.level === "error"
            ? "bg-red-950/60 border border-red-500/30 text-red-200"
            : "bg-neutral-950/80 border border-neutral-600/40 text-neutral-200",
        )}>
          {JSON.stringify(entry.meta, null, 2)}
        </pre>
      )}
    </div>
  );
}

// ─── Logs Tab ───────────────────────────────────────────

function LogsTab() {
  const { t } = useTranslation();
  const logs = useDebugStore((s) => s.logs);
  const scrollRef = useRef<HTMLDivElement>(null);
  const autoScrollRef = useRef(true);

  useEffect(() => {
    const el = scrollRef.current;
    if (el && autoScrollRef.current) {
      el.scrollTop = el.scrollHeight;
    }
  }, [logs.length]);

  const handleScroll = useCallback(() => {
    const el = scrollRef.current;
    if (!el) return;
    autoScrollRef.current = el.scrollTop + el.clientHeight >= el.scrollHeight - 40;
  }, []);

  if (logs.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-10 gap-3">
        <ScrollText className="h-10 w-10 text-neutral-500" />
        <p className="text-sm text-neutral-300 font-medium text-center max-w-[260px]">{t("debug.noLogs")}</p>
      </div>
    );
  }

  return (
    <div
      ref={scrollRef}
      onScroll={handleScroll}
      className="overflow-y-auto max-h-[45vh] -mx-4"
    >
      {logs.map((entry, i) => (
        <LogEntry key={`${entry.timestamp}-${i}`} entry={entry} />
      ))}
    </div>
  );
}

// ─── Main DebugPanel ────────────────────────────────────

export function DebugPanel() {
  const { t } = useTranslation();
  const tier = useTier();
  const enabled = useDebugStore((s) => s.enabled);
  const logCount = useDebugStore((s) => s.logs.length);
  const clearLogs = useDebugStore((s) => s.clearLogs);
  const [expanded, setExpanded] = useState(false);
  const [activeTab, setActiveTab] = useState<Tab>("account");
  // Badge and panel carry their own positions — a 480px panel can't reuse a badge's corner.
  const badgeDrag = useDraggable<HTMLButtonElement>();
  const panelDrag = useDraggable<HTMLDivElement>();

  // Simple accounts never get the debug panel, even with a persisted toggle.
  if (!enabled || !can(tier, "onchainDetail")) return null;

  // Collapsed badge — icon-only on mobile so it can't smother the bottom nav
  if (!expanded) {
    return (
      <button
        ref={badgeDrag.ref}
        {...badgeDrag.handleProps}
        onClick={() => setExpanded(true)}
        style={draggedStyle(badgeDrag)}
        className={cn(
          "fixed z-[60] flex items-center md:gap-2",
          // BottomNav is 5rem tall (+ safe area) below md, so bottom-5 parked
          // this badge on top of the "Profile" / "Admin" nav labels. Dragging
          // replaces this anchor with explicit coordinates.
          !badgeDrag.position &&
            "bottom-[calc(5.5rem+env(safe-area-inset-bottom))] right-4 md:bottom-5 md:right-5",
          "rounded-full bg-neutral-900/95 backdrop-blur-xl",
          "p-2.5 md:pl-3.5 md:pr-4 md:py-2.5",
          "text-sm font-semibold text-white touch-none",
          "shadow-lg shadow-black/30 border border-neutral-600",
          "hover:border-brand-400/60 hover:shadow-brand-500/10 hover:shadow-xl",
          badgeDrag.dragging ? "cursor-grabbing" : "active:scale-90 transition-all duration-150",
        )}
        aria-label={t("debug.openDebugPanel")}
        title={t("debug.openDebugPanel")}
      >
        <Bug className="h-5 w-5 text-brand-400" />
        <span className="hidden md:inline tracking-wide">DEBUG</span>
        {logCount > 0 && (
          <span
            className={cn(
              "flex items-center justify-center h-5 min-w-5 rounded-full bg-brand-500 px-1.5 text-[11px] font-bold text-white",
              "absolute -top-1.5 -right-1.5 md:static",
            )}
          >
            {logCount > 99 ? "99+" : logCount}
          </span>
        )}
      </button>
    );
  }

  const tabs: { id: Tab; label: string; icon: typeof Wallet }[] = [
    { id: "account", label: t("debug.accountInfo"), icon: Wallet },
    { id: "contracts", label: t("debug.contracts"), icon: FileCode },
    { id: "logs", label: `${t("debug.logs")}${logCount > 0 ? ` (${logCount})` : ""}`, icon: ScrollText },
  ];

  return (
    <div
      ref={panelDrag.ref}
      style={draggedStyle(panelDrag)}
      className={cn(
        "fixed z-[60] max-h-[60vh] md:max-h-[65vh]",
        !panelDrag.position &&
          "bottom-[calc(5.5rem+env(safe-area-inset-bottom))] right-4 left-4 md:bottom-5 md:right-5 md:left-auto",
        // Undragged, the mobile panel spans both gutters (w-auto). Once dragged it
        // is pinned by left/top alone, so it needs an explicit width or it would
        // shrink to its content.
        panelDrag.position ? "w-[calc(100vw-2rem)] md:w-[480px]" : "w-auto md:w-[480px]",
        "flex flex-col",
        "rounded-2xl",
        "bg-neutral-900/[0.98] backdrop-blur-xl",
        "text-neutral-100",
        "shadow-2xl shadow-black/50",
        "border border-neutral-600/60",
        "overflow-hidden",
      )}
      role="region"
      aria-label={t("debug.debugPanel")}
    >
      {/* Header — doubles as the panel's drag handle */}
      <div
        {...panelDrag.handleProps}
        className={cn(
          "flex items-center justify-between px-4 py-3.5 border-b border-neutral-700/60 touch-none select-none",
          panelDrag.dragging ? "cursor-grabbing" : "cursor-grab",
        )}
      >
        <div className="flex items-center gap-2.5">
          <div className="flex items-center justify-center h-8 w-8 rounded-lg bg-brand-500/25 border border-brand-400/20">
            <Bug className="h-[18px] w-[18px] text-brand-300" />
          </div>
          <span className="font-bold text-[17px] text-white font-sans">{t("debug.title")}</span>
          <span className="rounded-full bg-neutral-800 border border-neutral-600/50 px-2.5 py-0.5 text-[12px] font-semibold text-neutral-200">
            {getChainConfig().name}
          </span>
        </div>
        <div className="flex items-center gap-0.5">
          <button
            onClick={clearLogs}
            className="rounded-lg p-1.5 text-neutral-300 hover:text-white hover:bg-neutral-700/60 active:scale-85 transition-all duration-150"
            title={t("debug.clearLogs")}
            aria-label={t("debug.clearLogs")}
          >
            <Trash2 className="h-[18px] w-[18px]" />
          </button>
          <button
            onClick={() => setExpanded(false)}
            className="rounded-lg p-1.5 text-neutral-300 hover:text-white hover:bg-neutral-700/60 active:scale-85 transition-all duration-150"
            title={t("debug.collapse")}
            aria-label={t("debug.collapse")}
          >
            <ChevronDown className="h-[18px] w-[18px]" />
          </button>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex px-2 pt-1 border-b border-neutral-700/60" role="tablist">
        {tabs.map((tab) => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            role="tab"
            aria-selected={activeTab === tab.id}
            className={cn(
              "flex-1 flex items-center justify-center gap-1.5 py-2.5 text-[14px] font-sans font-semibold rounded-t-lg active:scale-95 transition-all duration-150 relative",
              activeTab === tab.id
                ? "text-brand-300"
                : "text-neutral-400 hover:text-neutral-100",
            )}
          >
            <tab.icon className="h-4 w-4" />
            {tab.label}
            {activeTab === tab.id && (
              <span className="absolute bottom-0 left-3 right-3 h-[2.5px] rounded-full bg-brand-400" />
            )}
          </button>
        ))}
      </div>

      {/* Tab content */}
      <div className="flex-1 overflow-y-auto p-4 min-h-0" role="tabpanel">
        {activeTab === "account" && <AccountTab />}
        {activeTab === "contracts" && <ContractsTab />}
        {activeTab === "logs" && <LogsTab />}
      </div>
    </div>
  );
}
